import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react'
import { Button } from '@/components/ui/button'
import styles from './fields.module.css'
import { createGeneralApi } from '../tbsGeneralApi'
import { cn, toCssLength, toStyleObject } from '@/lib/utils'

export default function WidgetField({ id, label, icon, visible, onEvent, compoGroup, iframeSrc, iframeId, fixedHeight, collapsible = false, collapsedOnLoad = false, toolbarBtns = [], userData, class: cssClass, style: rawStyle }) {
  // `visible: false` (JSON) → display:none posé directement sur le div [data-tbs-root] : voir
  // ChampField.jsx pour le détail (show()/hide() de tbsGeneralApi.js ciblent ce même élément).
  const visibilityStyle = visible === false ? { display: 'none' } : undefined
  const cssStyle = toStyleObject(rawStyle)
  const compoGroupRef = useRef(compoGroup ?? null)
  const userDataRef   = useRef(userData ?? null)
  const htmlRef       = useRef(null)
  const [collapsed, setCollapsed]     = useState(collapsedOnLoad)
  const [htmlContent, setHtmlContent] = useState(null)
  const [title, setTitle]             = useState(label)
  const isCollapsible = collapsible && !!title

  // Toolbar dans la barre de titre — voir CanvasField.jsx pour le détail du contrat/raisonnement
  // (même implémentation, dupliquée à l'identique).
  const [toolbarVisible, setToolbarVisible] = useState(true)
  const [toolbarState, setToolbarState]     = useState(toolbarBtns)
  const toolbarStateRef = useRef(toolbarBtns)
  useLayoutEffect(() => { toolbarStateRef.current = toolbarState }, [toolbarState])
  const setToolbarStateSynced = useCallback(updaterOrValue => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(toolbarStateRef.current) : updaterOrValue
    toolbarStateRef.current = next
    setToolbarState(next)
  }, [])

  useEffect(() => {
    window.tbsComponents = window.tbsComponents ?? {}
    window.tbsComponents[id] = {
      type: 'widget',
      ...createGeneralApi(id, userDataRef.current),
      getCompoGroup: () => compoGroupRef.current,
      setCompoGroup: v => { compoGroupRef.current = v },
      getHtml: () => htmlRef.current,
      setHtml: html => { htmlRef.current = html; setHtmlContent(html) },
      setTitle: t => setTitle(t),
      showToolbar:        ()                            => setToolbarVisible(true),
      hideToolbar:        ()                            => setToolbarVisible(false),
      addElemToolbar:     (btnId, icon, tip, vis, act) => setToolbarStateSynced(s => [...s, { id: btnId, icon, tooltip: tip, visible: vis, active: act }]),
      removeElemToolbar:  btnId                         => setToolbarStateSynced(s => s.filter(b => b.id !== btnId)),
      disableElemToolbar: btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, active: false } : b)),
      enableElemToolbar:  btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, active: true }  : b)),
      showElemToolbar:    btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, visible: true }  : b)),
      hideElemToolbar:    btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, visible: false } : b)),
      getListOfToolbarBtns: () => toolbarStateRef.current.map(b => b.id),
    }
    return () => { delete window.tbsComponents[id] }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  const toolbarButtons = toolbarVisible && toolbarState.length > 0 && (
    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
      {toolbarState.map(btn => (
        <Button
          key={btn.id}
          size="sm"
          variant="ghost"
          title={btn.tooltip}
          disabled={btn.active === false}
          // `size="sm"` (h-8) dépasse la hauteur naturelle du titre (py-2 + text-sm) — h-6 la
          // ramène à l'intérieur de la barre au lieu de l'agrandir.
          className="tbs-toolbar-btn-size h-6 px-2"
          style={{ visibility: btn.visible === false ? 'hidden' : 'visible' }}
          onClick={btn.active !== false ? () => onEvent(id, 'onFrameToolbarClick', 0, btn.id) : undefined}
        >
          {btn.icon && <i className={btn.icon} aria-hidden="true" />}
        </Button>
      ))}
    </div>
  )

  if (collapsed && isCollapsible) {
    return (
      <div id={id} data-tbs-root={id} className={cn('mb-2', cssClass)} style={{ ...cssStyle, ...visibilityStyle }}>
        <div className={styles.collapseBar} onClick={() => setCollapsed(false)} style={{ cursor: 'pointer', borderRadius: '0.1rem', border: '1px solid hsl(var(--border))' }}>
          <i className="fa-solid fa-chevron-right fa-xs" style={{ color: '#7a8499', flexShrink: 0 }} aria-hidden="true" />
          {icon && <i className={icon} style={{ color: '#7a8499', flexShrink: 0, marginLeft: 2 }} aria-hidden="true" />}
          <span className={styles.collapseBarLabel}>{title}</span>
        </div>
      </div>
    )
  }

  return (
    <div id={id} data-tbs-root={id} className={cn('mb-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] w-full overflow-hidden', cssClass)} style={{ ...cssStyle, ...visibilityStyle }}>
      {isCollapsible ? (
        <div className={`${styles.collapseBar} ${styles.collapseBarExpanded}`} onClick={() => setCollapsed(true)} style={{ cursor: 'pointer', borderRadius: '0.1rem 0.1rem 0 0' }}>
          <i className="fa-solid fa-chevron-down fa-xs" style={{ color: '#7a8499', flexShrink: 0 }} aria-hidden="true" />
          {icon && <i className={icon} style={{ color: '#7a8499', flexShrink: 0, marginLeft: 2 }} aria-hidden="true" />}
          <span className={styles.collapseBarLabel}>{title}</span>
          {toolbarButtons}
        </div>
      ) : title && (
        // Même couleur de fond que `.collapseBar` (hsl(var(--muted))) et que le titre non-
        // collapsible de CanvasField — le titre d'un widget doit avoir le même fond qu'un canvas.
        <h3 className="px-4 py-2 text-sm font-semibold text-[hsl(var(--foreground))] bg-[hsl(var(--muted))] border-b border-[hsl(var(--border))] flex items-center gap-2">
          <span className="flex-1 flex items-center gap-2 min-w-0">
            {icon && <i className={`${icon} text-[hsl(var(--muted-foreground))]`} aria-hidden="true" />}
            {title}
          </span>
          {toolbarButtons}
        </h3>
      )}
      <div className="p-2" style={fixedHeight ? { height: toCssLength(fixedHeight), overflowY: 'auto' } : undefined}>
        {htmlContent !== null
          ? <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
          : iframeSrc && (
            <iframe
              id={iframeId}
              src={iframeSrc}
              width="100%"
              height="100%"
              style={{ display: 'block', border: 'none' }}
            />
          )
        }
      </div>
    </div>
  )
}
